import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { AppProvider } from "@/contexts/AppContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Students from "@/pages/Students";
import Subjects from "@/pages/Subjects";
import Templates from "@/pages/Templates";
import YearWorkGrades from "@/pages/YearWorkGrades";
import FinalExamGrades from "@/pages/FinalExamGrades";
import Certificates from "@/pages/Certificates";
import Reports from "@/pages/Reports";
import Analysis from "@/pages/Analysis";
import Settings from "@/pages/Settings";
import SchoolsAdmin from "@/pages/SchoolsAdmin";
import Support from "@/pages/Support";
import ClassListPrint from "@/pages/ClassListPrint";
import SchoolStatsPrint from "@/pages/SchoolStatsPrint";
import TopStudentsPrint from "@/pages/TopStudentsPrint";
import SeatNumbersPrint from "@/pages/SeatNumbersPrint";
import SeatNumbersManage from "@/pages/SeatNumbersManage";
import Promotion from "@/pages/Promotion";
import Archive from "@/pages/Archive";
import GradeSheetPrint from "@/pages/GradeSheetPrint";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground font-display">جارٍ التحقق من الجلسة…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function SuperOnly({ children }) {
  const { user } = useAuth();
  if (user?.role !== "super_admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function TenantOnly({ children }) {
  const { user } = useAuth();
  if (user?.role === "super_admin") return <Navigate to="/schools" replace />;
  return children;
}

function DefaultRoute() {
  const { user } = useAuth();
  if (user?.role === "super_admin") return <Navigate to="/schools" replace />;
  return <Navigate to="/dashboard" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <Protected>
            <Layout />
          </Protected>
        }
      >
        <Route index element={<DefaultRoute />} />
        <Route path="dashboard" element={<TenantOnly><Dashboard /></TenantOnly>} />
        <Route path="schools" element={<SuperOnly><SchoolsAdmin /></SuperOnly>} />
        <Route path="support" element={<Support />} />
        <Route path="students" element={<TenantOnly><Students /></TenantOnly>} />
        <Route path="students/seat-numbers" element={<TenantOnly><SeatNumbersManage /></TenantOnly>} />
        <Route path="promotion" element={<TenantOnly><Promotion /></TenantOnly>} />
        <Route path="archive" element={<TenantOnly><Archive /></TenantOnly>} />
        <Route path="subjects" element={<TenantOnly><Subjects /></TenantOnly>} />
        <Route path="templates" element={<TenantOnly><Templates /></TenantOnly>} />
        <Route path="grades/year-work" element={<TenantOnly><YearWorkGrades /></TenantOnly>} />
        <Route path="grades/final-exam" element={<TenantOnly><FinalExamGrades /></TenantOnly>} />
        <Route path="certificates" element={<TenantOnly><Certificates /></TenantOnly>} />
        <Route path="reports" element={<TenantOnly><Reports /></TenantOnly>} />
        <Route path="print/class-lists" element={<TenantOnly><ClassListPrint /></TenantOnly>} />
        <Route path="print/school-stats" element={<TenantOnly><SchoolStatsPrint /></TenantOnly>} />
        <Route path="print/top-students" element={<TenantOnly><TopStudentsPrint /></TenantOnly>} />
        <Route path="print/seat-numbers" element={<TenantOnly><SeatNumbersPrint /></TenantOnly>} />
        <Route path="print/grade-sheets" element={<TenantOnly><GradeSheetPrint /></TenantOnly>} />
        <Route path="analysis" element={<TenantOnly><Analysis /></TenantOnly>} />
        <Route path="settings" element={<TenantOnly><Settings /></TenantOnly>} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <AppProvider>
              <AppRoutes />
              <Toaster position="top-center" richColors closeButton />
            </AppProvider>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
